export const M6_UNIT_3 = {
  id: "m6-u3-quick-decisions",
  title: "Unit 3: Making Quick Decisions",
  topic: "Master spontaneous decisions with WILL! Learn the difference between instant decisions (I'll do it!) and planned actions. Essential for natural, fluent conversation in daily life!",
  iconType: "lightning-bolt",
  grammarImage: "/grammar/grammar-m6-u3.jpg",
  grammarText: "Use WILL for instant decisions made while speaking. Different from GOING TO (already planned). Critical skill for offers, promises, and spontaneous reactions!",

  sets: [

    // ===================================================================
    // SET 1: INSTANT DECISIONS
    // ===================================================================
    {
      id: "m6-u3-s1",
      title: "Set 1: Instant Decisions",
      context: `*Toronto — Week 5, Sofía's apartment*

Sofía is getting more comfortable in Toronto. One evening, she and Priya are relaxing at home.
Suddenly, the phone rings. Without thinking, Sofía jumps up.
"I'll get it!" she says. It's Dan, asking if they want to go out for pizza.
"Sure! I'll come!" says Sofía without hesitation. Priya smiles. "You're getting very Canadian!"

*Toronto — Semana 5, apartamento de Sofía*

Sofía se está adaptando cada vez mejor a Toronto. Una noche, ella y Priya descansan en casa.
De repente, suena el teléfono. Sin pensarlo, Sofía se levanta de un salto.
"¡Yo lo contesto!" dice. Es Dan, preguntando si quieren salir a comer pizza.
"¡Claro, voy!" dice Sofía sin dudar. Priya sonríe. "¡Te estás volviendo muy canadiense!"`,

      questions: [
        {
          type: "match",
          q: "Match the situations to instant decisions:",
          pairs: [
            { left: "Phone is ringing", right: "I'll answer it!" },
            { left: "You see someone drop papers", right: "I'll help you!" },
            { left: "Friend asks for a ride", right: "I'll drive you!" }
          ],
          a: "completed",
          successTip: "Instant decisions use WILL 💡. When you decide RIGHT NOW — without planning before — use WILL (I'll). The decision happens in the moment of speaking. 'I'll answer it!' / 'I'll help you!' / 'I'll drive you!' — all spontaneous!"
        },
        {
          type: "mc",
          q: "The phone's ringing. What do you say?",
          options: ["I'll answer it!", "I'm going to answer it!", "I answer it!"],
          a: "I'll answer it!",
          tip: "You just decided NOW — spontaneous reaction = use WILL (I'll).",
          successTip: "'I'll answer it!' = the most natural response to a ringing phone 📞. It's an instant decision — you didn't plan it before. 'I'm going to answer it' sounds like you scheduled it. 'I'll' is spontaneous and natural!"
        },
        {
          type: "input",
          q: "Oh no! You dropped your keys. I ___ pick them up for you.",
          a: "will",
          alts: ["'ll", "Will","´ll"],
          tip: "Helping someone instantly = WILL. You just decided to help right now.",
          successTip: "Offering help the moment you see a problem = WILL 🤝. 'I will pick them up' / 'I'll pick them up' — both correct. In speech, 'I'll' is more natural. The key is: you decided RIGHT NOW, not before!"
        },
        {
          type: "order",
          q: "Traduce: ¡Te ayudaré! (you just decided to help)",
          words: ["I'll", "help", "you!"],
          a: "I'll help you!",
          extra: ["going", "to", "am"],
          tip: "Instant offer = I'll + base verb. No 'to' after will.",
          successTip: "'I'll help you!' = one of the most useful phrases in English 🤝. Universal, friendly, instant. Notice: no 'to' after WILL. 'I'll help' ✓ / 'I'll to help' ✗. Very common in homes, offices, and everyday life!"
        },
        {
          type: "mc",
          q: "It's cold in here. ___",
          options: ["I'll close the window.", "I'm going to close the window.", "I close the window."],
          a: "I'll close the window.",
          tip: "You noticed the cold just now → instant reaction = WILL.",
          successTip: "Reacting to something you just noticed = WILL ❄️🪟. You didn't plan to close the window — you felt the cold and decided instantly. 'I'm going to close it' means you already had that plan before noticing the cold. Big difference!"
        },
        {
          type: "input",
          q: "The doorbell's ringing. I ___ get it!",
          a: "will",
          alts: ["'ll", "Will"],
          tip: "Doorbell = instant reaction. Use WILL (I'll).",
          successTip: "'I'll get it!' = classic household phrase 🚪. Whether it's the phone or the door, 'I'll get it!' is the natural spontaneous offer. Short, clear, and very common in British and North American English!"
        },
        {
          type: "mc",
          q: "Your friend dropped their phone. You say instantly:",
          options: ["I'll grab it!", "I'm going to grab it!", "I grab it!"],
          a: "I'll grab it!",
          tip: "You saw it happen → instant reaction = WILL.",
          successTip: "'Grab' = informal way to say 'pick up' quickly 🤲. 'I'll grab it!' is more energetic than 'I'll get it!' Both are correct. 'Grab' implies speed — you're reacting fast. It's also used in 'grab a coffee', 'grab lunch', 'grab a seat'!"
        },
        {
          type: "order",
          q: "Traduce: Olvidé mi almuerzo. Comeré afuera. (decide now)",
          words: ["I", "forgot", "my", "lunch.", "I'll", "eat", "out."],
          a: "I forgot my lunch. I'll eat out.",
          extra: ["going", "to", "am"],
          tip: "Forgot lunch = new situation → instant decision = I'll eat out.",
          successTip: "'Eat out' = comer en restaurante 🍽️. Opposite: 'eat in' = comer en casa. Two sentences: past event (I forgot) → instant decision (I'll eat out). This is an extremely natural English pattern — something happens, so you decide instantly!"
        },
        {
          type: "mc",
          q: "You're at a restaurant and just decided. 'I think ___ the fish.'",
          options: ["I'll have", "I'm going to have", "I have"],
          a: "I'll have",
          tip: "Deciding at the restaurant table = instant decision = I'll have.",
          successTip: "'I'll have...' = the standard way to order food in English 🍴. Used in the USA, UK, Canada, and Australia. It's an instant decision made at the table. 'I'm going to have' sounds like you planned your order before arriving — which sounds odd!"
        },
        {
          type: "input",
          q: "That bag looks heavy. I ___ carry it for you.",
          a: "will",
          alts: ["'ll", "Will"],
          tip: "Polite offer made right now = WILL. You just noticed the heavy bag.",
          successTip: "Polite offers = WILL 💪. You see the situation (heavy bag) and instantly offer to help. 'I'll carry it for you' is very kind and natural. Very common phrase in everyday English — a small act of kindness expressed with WILL!"
        }
      ]
    },

    // ===================================================================
    // SET 2: SPONTANEOUS vs PLANNED
    // ===================================================================
    {
      id: "m6-u3-s2",
      title: "Set 2: Spontaneous vs Planned",
      context: `*Toronto — Week 5, Saturday*

Sofía is making weekend plans with Priya. They already have a concert booked for Sunday.
But right now, Dan texts: "Anyone free tonight?"
Priya already has plans. But Sofía sees the text and decides instantly.
"I'll go!" she replies. Priya laughs. "You're always so spontaneous, Sofía. That's why we love you!"

*Toronto — Semana 5, sábado*

Sofía hace planes para el fin de semana con Priya. Ya tienen un concierto reservado para el domingo.
Pero ahora Dan escribe: "¿Alguien está libre esta noche?"
Priya ya tiene planes. Pero Sofía ve el mensaje y decide al instante.
"¡Voy!" responde. Priya se ríe. "Siempre eres tan espontánea, Sofía. ¡Por eso te queremos!"`,

      questions: [
        {
          type: "mc",
          q: "Priya bought concert tickets yesterday. Tomorrow she ___ to the concert.",
          options: ["is going", "will go", "'ll go"],
          a: "is going",
          tip: "Tickets already bought = plan made before = GOING TO (not WILL).",
          successTip: "Tickets bought = plan already made 🎫. Use GOING TO (or present continuous). WILL is for instant decisions. 'I'm going to the concert' shows a pre-made plan. NOT 'I'll go' — that would sound like you just decided this second!"
        },
        {
          type: "mc",
          q: "Oh! That concert looks amazing! I think ___ buy tickets!",
          options: ["I'll", "I'm going to", "I'm"],
          a: "I'll",
          tip: "Just saw it and decided NOW = instant decision = WILL.",
          successTip: "Just saw it → I'll buy = instant decision 💡. Compare: 'I'll buy tickets' (just decided seeing the ad) vs 'I'm going to buy tickets' (you researched it and decided before). Same result — different moment of decision. That's the WILL vs GOING TO rule!"
        },
        {
          type: "order",
          q: "Traduce: Tengo hambre. Pediré pizza. (decide right now)",
          words: ["I'm", "hungry.", "I'll", "order", "pizza."],
          a: "I'm hungry. I'll order pizza.",
          extra: ["going", "to", "am"],
          tip: "Hungry NOW → decide NOW = I'll order (not going to).",
          successTip: "'I'll order pizza' = instant decision triggered by hunger 🍕. If you had said 'I'm going to order pizza tonight' earlier today, THAT would be GOING TO. The trigger (I'm hungry) shows the decision is happening right now — perfect for WILL!"
        },
        {
          type: "match",
          q: "Match the context to the correct future form:",
          pairs: [
            { left: "Phone rings (instant)", right: "I'll answer it" },
            { left: "Scheduled meeting", right: "I'm going to attend" },
            { left: "Just decided to help", right: "I'll do it" }
          ],
          a: "completed",
          successTip: "The core rule: Instant reaction = WILL 💡 / Already planned = GOING TO 📅. This is the most important distinction in Unit 3. Master it and your English sounds fluent and natural in any situation!"
        },
        {
          type: "mc",
          q: "My plans for next week: I ___ visit my grandmother.",
          options: ["am going to", "will", "'ll"],
          a: "am going to",
          tip: "Plans already decided for next week = GOING TO (not WILL).",
          successTip: "Future plans you already decided = GOING TO 📅. 'I'm going to visit my grandmother next week' shows a pre-made plan. WILL would suggest you're deciding right now as you speak — which isn't the case when you say 'my plans for next week'!"
        },
        {
          type: "input",
          q: "That movie looks great! I think I ___ watch it tonight. (decide now)",
          a: "will",
          alts: ["'ll", "Will"],
          tip: "Just saw the movie poster and decided NOW = WILL.",
          successTip: "'I think I'll watch it tonight' = deciding right now after seeing something 🎬. 'I think I'll...' softens the instant decision slightly — you're considering it as you speak. Very natural expression for spontaneous evening plans!"
        },
        {
          type: "mc",
          q: "Which shows an INSTANT decision?",
          options: ["I'll make dinner!", "I'm going to make dinner.", "I made dinner."],
          a: "I'll make dinner!",
          tip: "I'll = just decided this second! I'm going to = planned before.",
          successTip: "I'll make dinner! = spontaneous decision 💡 / I'm going to make dinner = pre-planned 📋. The exclamation mark hints at spontaneity. Crucial grammar difference — 'I'll' sounds energetic and immediate, 'I'm going to' sounds organized and deliberate!"
        },
        {
          type: "order",
          q: "Traduce: Ya compré boletos. Voy a ir al concierto. (plan made)",
          words: ["I", "already", "bought", "tickets.", "I'm", "going", "to", "the", "concert."],
          a: "I already bought tickets. I'm going to the concert.",
          extra: ["will", "'ll", "I'll"],
          tip: "Already bought = evidence of pre-made plan = GOING TO.",
          successTip: "'Already' signals a completed action that confirms a plan 🎫. 'I already bought tickets' = proof of the plan → GOING TO. WILL would mean you're deciding to go right now — but you already decided when you bought the tickets!"
        },
        {
          type: "mc",
          q: "Someone asks: 'Can you help me?' Your instant reply:",
          options: ["Sure! I'll help you.", "Sure! I'm going to help you.", "Sure! I help you."],
          a: "Sure! I'll help you.",
          tip: "They JUST asked → you decide RIGHT NOW = instant response = WILL.",
          successTip: "'Sure! I'll help you.' = instant agreement 👍. You can also say 'Sure! I'll do it'"
        },
        {
          type: "input",
          q: "I have a reservation for 7 PM. I ___ eat at that restaurant tonight. (plan)",
          a: "am going to",
          alts: ["'m going to", "am gonna", "'m gonna"],
          tip: "Reservation = pre-made plan = GOING TO (not WILL).",
          successTip: "A reservation is evidence of a pre-made plan 🍽️. Use GOING TO. If someone asks 'Where are you eating tonight?' and you already have a reservation, you say 'I'm going to [restaurant]' — not 'I'll eat there' (that would sound like you just decided)!"
        }
      ]
    },

    // ===================================================================
    // SET 3: USING SO
    // ===================================================================
    {
      id: "m6-u3-s3",
      title: "Set 3: Using SO",
      context: `*Toronto — Week 6, Monday morning*

It's a cold Monday. Sofía's bus is late and it's starting to rain.
She looks at the sky, then at her watch. "The bus is late, so I'll take a taxi," she tells Priya.
Later at school, she realizes she forgot her lunch. "I forgot my lunch, so I'll grab something nearby."
Priya laughs. "You're becoming an expert at making instant decisions, Sofía!"

*Toronto — Semana 6, lunes por la mañana*

Es un lunes frío. El autobús de Sofía se retrasa y empieza a llover.
Ella mira el cielo y luego su reloj. "El autobús se retrasa, así que tomaré un taxi", le dice a Priya.
Más tarde en la escuela, se da cuenta de que olvidó su almuerzo. "Olvidé mi almuerzo, así que agarraré algo por aquí."
Priya ríe. "¡Te estás volviendo experta en tomar decisiones al instante, Sofía!"`,

      questions: [
        {
          type: "mc",
          q: "There's no milk, ___ I'll go to the store.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "SO connects: situation (no milk) → decision (I'll go to store).",
          successTip: "SO = consequence connector 🔗. Pattern: SITUATION + SO + DECISION. 'There's no milk' (situation) → 'so' → 'I'll go to the store' (instant decision). SO links the problem to the spontaneous solution. Very natural in everyday English!"
        },
        {
          type: "order",
          q: "Traduce: Está lloviendo, así que tomaré un taxi.",
          words: ["It's", "raining,", "so", "I'll", "take", "a", "taxi."],
          a: "It's raining, so I'll take a taxi.",
          extra: ["because", "but", "and"],
          tip: "Rain (situation) + SO + take taxi (instant decision).",
          successTip: "Rain + taxi = classic instant decision in English 🌧️🚕. Notice the comma before 'so' — it separates the two clauses. 'It's raining, so I'll take a taxi' is one of the most natural sentences in everyday English. Perfect SO + WILL structure!"
        },
        {
          type: "input",
          q: "I'm tired, ___ I'll stay home tonight.",
          a: "so",
          alts: ["So"],
          tip: "SO links: tired (situation) → stay home (decision).",
          successTip: "Tired → stay home = very natural cause and effect 🏠. 'So' is informal and conversational. In formal writing you'd use 'therefore' or 'as a result', but in spoken English 'so' is perfect and extremely common!"
        },
        {
          type: "mc",
          q: "I'm feeling bad today. I think I'll ___ today.",
          options: ["stay in", "going home", "not go out"],
          a: "stay in",
          tip: "'Stay in' = phrasal verb meaning to stay at home. Very common in British/Canadian English.",
          successTip: "'Stay in' = quedarse en casa (phrasal verb) 🏠. Opposite: 'go out'. Both 'stay in' and 'stay home' are correct, but 'stay in' is more idiomatic in British and Canadian English. 'I think I'll stay in tonight' is very natural!"
        },
        {
          type: "order",
          q: "Traduce: El restaurante está cerrado, así que comeremos en casa.",
          words: ["The", "restaurant", "is", "closed,", "so", "we'll", "eat", "at", "home."],
          a: "The restaurant is closed, so we'll eat at home.",
          extra: ["but", "because", "and"],
          successTip: "'We'll eat at home' = instant group decision. Clean, natural, and very common!"
        },
        {
          type: "mc",
          q: "There's nothing on TV, so I think I'll ___.",
          options: ["go out", "stay", "am going"],
          a: "go out",
          tip: "'Go out' = phrasal verb meaning to leave the house. Opposite of 'stay in'.",
          successTip: "'Go out' = salir de casa 🚶. Opposite of 'stay in'. Very common phrasal verb."
        },
        {
          type: "input",
          q: "My car won't start, ___ I'll take the bus.",
          a: "so",
          alts: ["So", "I think", "I guess","Maybe"],
          tip: "Car problem (situation) → SO → bus (instant decision).",
        },
        {
          type: "mc",
          q: "I forgot my lunch, so I'll ___ something nearby.",
          options: ["grab", "take", "do"],
          a: "grab",
          tip: "'Grab' = informal way to say 'get quickly'. Very common in casual English.",
          successTip: "'Grab something' = conseguir algo rápido 🥪. 'Grab lunch' / 'Grab a coffee' / 'Grab a bite' = very typical phrases. — you're not sitting down for a full meal, just getting something quickly."
        },
        {
          type: "order",
          q: "Traduce: Es tarde, así que llamaré un Uber.",
          words: ["It's", "late,", "so", "I'll", "call", "an", "Uber."],
          a: "It's late, so I'll call an Uber.",
          extra: ["because", "but", "therefore"],
          successTip: "Uber is used in 70+ countries 🚗. 'Call an Uber' = pedir un Uber. Notice: 'an Uber' (not 'a Uber') because 'Uber' starts with a vowel sound. 'It's late, so I'll call an Uber' = perfect SO + WILL sentence. Modern and very natural!"
        },
        {
          type: "match",
          q: "Match situations to decisions with SO:",
          pairs: [
            { left: "I'm broke", right: "so I'll stay in" },
            { left: "It's sunny", right: "so I'll go out" },
            { left: "I'm starving", right: "so I'll grab a bite" }
          ],
          a: "completed",
          successTip: "Three key informal expressions! 💰 'I'm broke' = no tengo dinero (slang). ☀️ 'It's sunny' → logical to go out. 😋 'I'm starving' = tengo muchísima hambre. 🍽️ 'Grab a bite' = comer algo rápido. All very natural in everyday English conversation!"
        }
      ]
    },

    // ===================================================================
    // SET 4: IN THAT CASE
    // ===================================================================
    {
      id: "m6-u3-s4",
      title: "Set 4: In That Case",
      context: `*Toronto — Week 6, university café*

Sofía is having coffee with her classmates Leo and Amara. The café runs out of coffee.
"In that case, I'll have tea," says Sofía politely. Leo is impressed.
"Where did you learn to say that?"
"From my Canadian friends," Sofía smiles. "It sounds more elegant than just 'so'!"

*Toronto — Semana 6, cafetería universitaria*

Sofía toma café con sus compañeros Leo y Amara. La cafetería se queda sin café.
"En ese caso, tomaré té," dice Sofía con educación. Leo queda impresionado.
"¿Dónde aprendiste a decir eso?"
"De mis amigos canadienses," sonríe Sofía. "¡Suena más elegante que solo 'so'!"`,

      questions: [
        {
          type: "mc",
          q: "The trains aren't running. ___ we'll take the bus.",
          options: ["In that case,", "But", "Because"],
          a: "In that case,",
          tip: "'In that case' = more formal than 'so'. Both correct, different register.",
          successTip: "'In that case' = dado el caso, entonces 🎩. More formal and polished than 'so'. Both mean the same thing — they connect a situation to a decision. 'So' is casual. 'In that case' is for polite conversation, professional settings, or when you want to sound educated!"
        },
        {
          type: "order",
          q: "Traduce: No hay café. En ese caso, tomaré té.",
          words: ["There's", "no", "coffee.", "In", "that", "case,", "I'll", "have", "tea."],
          a: "There's no coffee. In that case, I'll have tea.",
          extra: ["so", "because", "but"],
          tip: "'In that case' starts a new sentence here. Always followed by a comma.",
          successTip: "'In that case, I'll have tea' = very polite and natural 🍵. Notice the comma after 'case' — it's always there. This phrase is especially common in British English. A small phrase that instantly makes your English sound more sophisticated!"
        },
        {
          type: "input",
          q: "The movie is sold out. In that case, we'll ___ a different one.",
          a: "watch",
          alts: ["Watch", "see", "See", "pick", "Pick", "choose", "Choose"],
          tip: "'Sold out' = no tickets left. Decide what to do instead.",
          successTip: "'Sold out' = agotado (sin entradas) 🎫. Very common at cinemas, concerts, and sports events."
        },
        {
          type: "mc",
          q: "You can't make it tonight. In that case, I'll ___.",
          options: ["take a rain check", "go alone", "stay home"],
          a: "take a rain check",
          tip: "'Take a rain check' = reschedule politely for another day.",
          successTip: "'Take a rain check' = posponer una invitación educadamente."
        },
          {
          type: "order",
          q: "Traduce: Está lloviendo. En ese caso, llevaré paraguas.",
          words: ["It's", "raining.", "In", "that", "case,", "I'll", "take", "an", "umbrella."],
          a: "It's raining. In that case, I'll take an umbrella.",
          extra: ["so", "but", "because"],
        },
        {
          type: "mc",
          q: "The meeting is cancelled. In that case, I'll ___.",
          options: ["head home", "going home", "leave"],
          a: "head home",
          tip: "'Head home' sounds more natural and native than just 'go home'.",
          successTip: "'Head home' = dirigirse a casa 🏠. More natural than just 'go home'"
        },
        {
          type: "input",
          q: "You're busy tomorrow. In that case, we'll meet ___ week.",
          a: "next",
          alts: ["Next", "another","the following"],
          tip: "'Next week' = la semana que viene. One word.",
        },
        {
          type: "mc",
          q: "Your friend invited you to a party, but you have overtime at work. You say politely:",
          options: ["Can I take a rain check?", "I can't go.", "I'll miss it."],
          a: "Can I take a rain check?",
          tip: "'Take a rain check' = reschedule for another day. Polite and very common.",
          successTip: "'Can I take a rain check?' = ¿Podemos dejarlo para otro día? 🎟️. Comes from baseball — if rain cancelled a game, fans got a ticket for another day. Now used for any rescheduled plan. Very common in North American English. Polite and friendly!"
        },
        {
          type: "match",
          q: "Match the situations to decisions using 'In that case':",
          pairs: [
            { left: "You're not hungry", right: "I'll eat alone" },
            { left: "The gym is closed", right: "I'll work out at home" },
            { left: "You can't come", right: "I'll take a rain check" }
          ],
          a: "completed",
          successTip: "'Work out' = hacer ejercicio 💪. Phrasal verb for gym/fitness. 'Work out at home' = very common during travel or when gyms are closed. 'In that case, I'll work out at home' = polished, adaptable English. Great phrase for health conversations!"
        },
        {
          type: "order",
          q: "Traduce: El vuelo está retrasado. En ese caso, esperaré en la cafetería.",
          words: ["The", "flight", "is", "delayed.", "In", "that", "case,", "I'll", "wait", "in", "the", "café."],
          a: "The flight is delayed. In that case, I'll wait in the café.",
          extra: ["so", "because", "but"],
        }
      ]
    },

    // ===================================================================
    // SET 5: OFFERS & PROMISES
    // ===================================================================
    {
      id: "m6-u3-s5",
      title: "Set 5: Offers & Promises",
      context: `*Toronto — Week 7, university library*

Sofía is helping her classmates before their midterm exams. Leo is struggling with grammar.
"Don't worry," says Sofía. "I'll explain it to you." Leo looks relieved.
Later, Dan texts: "Can you pick me up at the station tomorrow?"
"Of course! I'll be there at 6," Sofía replies. She's becoming the most reliable friend in the group.

*Toronto — Semana 7, biblioteca universitaria*

Sofía ayuda a sus compañeros antes de sus exámenes parciales. Leo tiene dificultades con la gramática.
"No te preocupes," dice Sofía. "Te lo explico." Leo se ve aliviado.
Más tarde, Dan le escribe: "¿Puedes recogerme en la estación mañana?"
"¡Claro! Estaré ahí a las 6," responde Sofía. Se está convirtiendo en la amiga más confiable del grupo.`,

      questions: [
        {
          type: "mc",
          q: "Can you help me move this table? '___'",
          options: ["Sure! I'll help you.", "Sure! I'm going to help you.", "Sure! I help you."],
          a: "Sure! I'll help you.",
          tip: "They just asked → you decide RIGHT NOW = instant offer = WILL.",
          successTip: "'Sure! I'll help you.' = instant friendly agreement 🤝. Perfect structure: Sure (informal yes) + I'll (instant decision) + help you (action). Very polite and natural. 'I'm going to help you' sounds pre-planned — strange for a reaction to a question you just heard!"
        },
        {
          type: "order",
          q: "Traduce: Te llamaré mañana. (promise)",
          words: ["I'll", "call", "you", "tomorrow."],
          a: "I'll call you tomorrow.",
          extra: ["going", "to", "am"],
          tip: "Promises always use WILL (I'll). Not GOING TO.",
          successTip: "'I'll call you tomorrow' = one of the most common promises in English 📞. WILL for promises — always. It sounds sincere and committed. 'I'm going to call you' sounds like a plan you made, not a promise you're making right now. WILL is the promise tense!"
        },
        {
          type: "input",
          q: "Don't worry! I ___ be late. (promise — negative)",
          a: "won't",
          alts: ["Won't", "will not", "Will not"],
          tip: "Negative promise = WON'T (will not). Very common reassurance.",
          successTip: "'I won't be late' = negative promise 🚫. Won't = will not. 'Don't worry, I won't be late!' Sounds sincere and committed. Much more natural than 'I will not be late' (too formal/emphatic)!"
        },
        {
          type: "mc",
          q: "You look tired. I'll ___.",
          options: ["give you a hand", "give help you", "assist you"],
          a: "give you a hand",
          tip: "'Give you a hand' = idiomatic expression for 'help'. More native-sounding.",
          successTip: "'Give you a hand' = ayudarte 🤚. Idiomatic and very natural. 'Assist you' is too formal for casual conversation."
        },
        {
          type: "order",
          q: "Traduce: No te preocupes. Te recogeré en el aeropuerto. (promise)",
          words: ["Don't", "worry.", "I'll", "pick", "you", "up", "at", "the", "airport."],
          a: "Don't worry. I'll pick you up at the airport.",
          extra: ["going", "to", "am"],
          tip: "'Pick up' = phrasal verb meaning to collect someone and give them a ride.",
          successTip: "'Pick you up' = recogerte 🚗. Essential phrasal verb for transportation. 'Pick up' = ir a buscar a alguien. 'Drop off' = dejar a alguien. These two phrasal verbs go together — master both! 'I'll pick you up at 7 and drop you off at the station.'"
        },
        {
          type: "mc",
          q: "I promise I'll ___ the money next week.",
          options: ["pay you back", "return you", "give back"],
          a: "pay you back",
          tip: "'Pay back' = the most natural phrasal verb for returning money.",
          successTip: "'Pay you back' = devolverte el dinero 💰. Phrasal verb specifically for money. 'Return' sounds too formal for this. 'Give back' is less natural than 'pay back' for money."
        },
        {
          type: "input",
          q: "Complete the offer: I'll ___ you a ride home.",
          a: "give",
          alts: ["Give"],
          tip: "'Give someone a ride' = llevar a alguien en carro. US English. UK: 'give a lift'.",
          successTip: "'Give you a ride' = llevarte en carro 🚗. US English. In UK English: 'give you a lift'. Both mean the same! 'Can I give you a ride home?' / 'I'll give you a lift.'"
        },
        {
          type: "mc",
          q: "Thanks for lending me money! I'll ___.",
          options: ["pay you back soon", "give it tomorrow", "return fast"],
          a: "pay you back soon",
          tip: "'Pay you back soon' = the most natural and complete promise for money.",
          successTip: "Lending/borrowing vocabulary 💵: 'Lend' = prestar. 'Borrow' = pedir prestado. 'Pay back' = devolver dinero. 'I'll pay you back soon!' = standard promise after borrowing. Very important phrase — everyone needs it at some point!"
        },
        {
          type: "match",
          q: "Match the offers using phrasal verbs:",
          pairs: [
            { left: "You need a ride?", right: "I'll drop you off" },
            { left: "You're busy?", right: "I'll help out" },
            { left: "You need to talk?", right: "I'll call you later" }
          ],
          a: "completed",
          successTip: "Three essential phrasal verbs! 🚗 'Drop off' = dejar a alguien en un lugar. 🤝 'Help out' = echar una mano (implies extra effort). 📞 'Call you later' = llamarte más tarde. Notice: 'help out' is slightly stronger than just 'help' — it implies going out of your way!"
        },
        {
          type: "order",
          q: "Traduce: Te prometo que no lo olvidaré.",
          words: ["I", "promise", "I", "won't", "forget", "it."],
          a: "I promise I won't forget it.",
          extra: ["going", "to", "am"],        }
      ]
    },

    // ===================================================================
    // SET 6: I THINK I'LL...
    // ===================================================================
    {
      id: "m6-u3-s6",
      title: "Set 6: I Think I'll...",
      context: `*Toronto — Week 8, Friday evening*

It's the end of the week and Sofía doesn't have firm plans yet.
She's considering her options. "I think I'll go to the farmers' market tomorrow," she tells Priya.
"Or maybe I'll just hang out here." Priya smiles.
"'I think I'll' — you use that all the time now. It's so natural!"

*Toronto — Semana 8, viernes por la noche*

Es el fin de semana y Sofía aún no tiene planes definitivos.
Está considerando sus opciones. "Creo que iré al mercado de productores mañana," le dice a Priya.
"O quizás me quede aquí." Priya sonríe.
"'I think I'll' — lo dices todo el tiempo ahora. ¡Suena tan natural!"`,

      questions: [
        {
          type: "mc",
          q: "I'm tired. I think I'll ___.",
          options: ["call it a night", "going to bed", "sleeping"],
          a: "call it a night",
          tip: "'Call it a night' = idiomatic expression meaning to stop and go to sleep.",
          successTip: "'Call it a night' = terminar el día e irse a dormir 🌙. One of the most common English idioms for ending an evening. More expressive than 'go to bed'. 'I'm beat — I think I'll call it a night' sounds completely native. Master this idiom!"
        },
        {
          type: "order",
          q: "Traduce: Tengo hambre. Creo que pediré pizza.",
          words: ["I'm", "hungry.", "I", "think", "I'll", "order", "pizza."],
          a: "I'm hungry. I think I'll order pizza.",
          extra: ["going", "to", "am"],
          tip: "'I think I'll' = considering a decision right now. Not 100% committed yet.",
          successTip: "'I think I'll order pizza' = tentative decision 🍕. You're considering it as you speak — not 100% decided. Compare: 'I'll order pizza' (decided!) vs 'I think I'll order pizza' (still thinking)"
        },
        {
          type: "input",
          q: "There are so many options on the menu. I think I'll ___ the fish.",
          a: "have",
          alts: ["Have", "get", "Get", "order", "Order", "try", "Try"],
          tip: "Deciding at the restaurant = instant/tentative decision. Several verbs work.",
          successTip: "'I'll have...' = most classic way to order 🍽️. 'I'll get...' = more casual. 'I'll try...' = shows curiosity about something new. 'I'll order...' = also natural. All four are correct — they just have slightly different feels. 'Have' is the safest choice!"
        },
        {
          type: "mc",
          q: "This movie looks boring. I think I'll ___.",
          options: ["take off", "leave", "go"],
          a: "take off",
          tip: "'Take off' = informal phrasal verb meaning to leave.",
          successTip: "'Take off' = irse/marcharse 🚶. Informal and very common. 'I'm taking off' = me voy. More casual than 'leave'. You'll hear it constantly: 'I think I'll take off now' / 'I'm going to take off' / 'She took off early.' Great addition to your phrasal verb list!"
        },
        {
          type: "order",
          q: "Traduce: Hace calor. Creo que me pondré shorts.",
          words: ["It's", "hot.", "I", "think", "I'll", "put", "on", "my", "shorts."],
          a: "It's hot. I think I'll put on my shorts.",
          extra: ["wears", "going", "to"],
          tip: "'Put on' = phrasal verb for wearing clothes. Opposite: 'take off'.",
          successTip: "'Put on' = ponerse (ropa) 👕. Opposite: 'take off' = quitarse. These two phrasal verbs are essential for clothes!"
        },
        {
          type: "mc",
          q: "I'm not sure yet, but I think I'll ___ the party.",
          options: ["skip", "miss", "not go to"],
          a: "skip",
          tip: "'Skip' = informal way to say 'not attend' something intentionally.",
          successTip: "'Skip' = saltarse algo intencionalmente 🚫. 'Miss' = no poder ir (accidental). 'I'll skip the party' = I choose not to go. 'I'll miss the party' = I can't go (implies I want to but can't). Important distinction! 'Skip class' / 'skip the meeting' = very common!"
        },
        {
          type: "input",
          q: "It's getting late. I think I'll ___ now. (informal: leave)",
          a: "head out",
          alts: ["Head out", "leave", "Leave", "take off", "Take off", "head home", "Head home", "go", "Go"],
          tip: "'Head out' = informal phrasal verb for leaving a place.",
          successTip: "'Head out' = salir/irse 🚪. Very natural and casual. 'I'm heading out' = me voy. 'Head home' = go home. 'Head out' = leave in general. Both are extremely common in North American English. Much more natural than simply 'I will go' or 'I will leave'!"
        },
        {
          type: "mc",
          q: "I'm free this weekend. I think I'll ___.",
          options: ["hang out with friends", "be with friends", "see friends"],
          a: "hang out with friends",
          tip: "'Hang out' = spend casual, relaxed time together. Very common phrasal verb.",
          successTip: "'Hang out' = pasar tiempo juntos de forma relajada 👫. Super common in North American English. 'Wanna hang out?' = ¿Quieres salir/vernos? 'We just hung out' = solo estuvimos juntos sin hacer nada especial. Essential social phrasal verb!"
        },
        {
          type: "match",
          q: "Match tentative decisions with 'I think I'll...':",
          pairs: [
            { left: "Tired of cooking", right: "I'll eat out" },
            { left: "Bored at home", right: "I'll go out" },
            { left: "Need groceries", right: "I'll stop by the store" }
          ],
          a: "completed",
          successTip: "'Stop by' = pasar brevemente por un lugar 🏪. 'I'll stop by the store' = voy a pasar por la tienda rápido. Very common in daily life! 'Stop by' implies a quick, casual visit — you're not going somewhere as a main destination, just passing through. Very natural!"
        },
        {
          type: "order",
          q: "Traduce: Estoy aburrido. Creo que saldré con amigos.",
          words: ["I'm", "bored.", "I", "think", "I'll", "hang", "out", "with", "friends."],
          a: "I'm bored. I think I'll hang out with friends.",
          extra: ["going", "to", "am"],
        }
      ]
    },

    // ===================================================================
    // SET 7: RESTAURANT & SHOPPING DECISIONS
    // ===================================================================
    {
      id: "m6-u3-s7",
      title: "Set 7: Restaurant & Shopping",
      context: `*Toronto — Week 8, Saturday*

Sofía goes to the Eaton Centre mall with Priya. They stop at a restaurant for lunch first.
The waiter asks what they'd like. "I'll have the salmon, please," says Sofía confidently.
Afterwards they go shopping. Sofía finds a beautiful coat. "I'll take it!" she says immediately.
Priya claps. "You made both decisions in under five seconds. True Canadian speed!"

*Toronto — Semana 8, sábado*

Sofía va al centro comercial Eaton Centre con Priya. Primero paran en un restaurante a almorzar.
El mesero pregunta qué quieren. "Tomaré el salmón, por favor," dice Sofía con confianza.
Después van de compras. Sofía encuentra un abrigo hermoso. "¡Me lo llevo!" dice de inmediato.
Priya aplaude. "Tomaste ambas decisiones en menos de cinco segundos. ¡Velocidad canadiense de verdad!"`,

      questions: [
        {
          type: "mc",
          q: "At a restaurant: 'Are you ready to order?' '___'",
          options: ["Yes, I'll have the steak.", "Yes, I'm going to have the steak.", "Yes, I have the steak."],
          a: "Yes, I'll have the steak.",
          tip: "Deciding at the table = instant decision = I'll have. Standard restaurant English.",
          successTip: "'I'll have...' = THE standard ordering phrase in English 🍴. Used in all English-speaking countries. Waiters expect it. 'I'm going to have' = grammatical but unusual in restaurants. 'I have' = present tense, completely wrong for ordering. Always: 'I'll have...'"
        },
        {
          type: "order",
          q: "Traduce: Para el postre, probaré el pastel de chocolate.",
          words: ["For", "dessert,", "I'll", "try", "the", "chocolate", "cake."],
          a: "For dessert, I'll try the chocolate cake.",
          extra: ["have", "going", "to"],
          tip: "'Try' = good when ordering something new. Shows adventurousness.",
          successTip: "'I'll try...' = good for new or unfamiliar foods 🍰. Shows openness. 'For dessert, I'll try...' = very natural restaurant phrase. 'Try' suggests you haven't had it before and you're curious. Much more interesting than just 'I'll have' — it tells a story!"
        },
        {
          type: "input",
          q: "This shirt looks nice. I think I'll ___ it.",
          a: "get",
          alts: ["Get", "buy", "Buy", "take", "Take", "grab", "Grab"],
          tip: "'Get it' or 'buy it' — both natural for shopping decisions.",
          successTip: "Shopping vocabulary 🛍️: 'Get it' = most casual. 'Buy it' = standard. 'Take it' = very common in stores. 'Grab it' = impulse buy. All four are correct and natural. 'I think I'll get it' / 'I think I'll take it' — deciding in the moment = WILL. Perfect!"
        },
        {
          type: "mc",
          q: "The menu looks great! I think I'll ___ the seafood platter.",
          options: ["go for", "make", "do"],
          a: "go for",
          tip: "'Go for' = idiomatic way to say 'choose'. Very common in restaurants.",
          successTip: "'Go for' = elegirse por algo / decidirse 🦐. Very idiomatic and natural. 'I'll go for the fish' / 'I'll go for the salad' = extremely common in restaurants. 'Choose' and 'pick' are correct but more literal. 'Go for' sounds most native in a restaurant setting!"
        },
        {
          type: "order",
          q: "Traduce: Esto es muy caro. Creo que lo dejaré pasar.",
          words: ["This", "is", "too", "expensive.", "I", "think", "I'll", "pass."],
          a: "This is too expensive. I think I'll pass.",
          extra: ["going", "not", "to"],
          tip: "'Pass' = informal way to decline. Common in shopping and restaurants.",
          successTip: "'I'll pass' = no, gracias — lo dejo pasar 🙅. Very common informal decline. 'I'll pass on the dessert' / 'I'll pass on that one.' Polite and light — you're not rude, just choosing not to. Much more natural than 'No, I don't want it.' Use it constantly!"
        },
        {
          type: "mc",
          q: "Shopping: 'Do you want to try it on?' 'Yes, ___'",
          options: ["I'll try it on.", "I'm going to try it on.", "I try it on."],
          a: "I'll try it on.",
          tip: "Instant decision to try the item = WILL.",
          successTip: "'Try on' = probarse ropa 👔. Essential phrasal verb for shopping. 'I'll try it on' = instant decision in the store. Notice: 'try it ON' — the pronoun goes between 'try' and 'on'. 'Try on the jacket' OR 'try it on' — never 'try on it'. Important word order rule!"
        },
        {
          type: "input",
          q: "These shoes are perfect! I'll ___ them!",
          a: "take",
          alts: ["Take", "buy", "Buy", "get", "Get", "grab", "Grab"],
          tip: "'I'll take them' = the most classic phrase when buying something in a store.",
          successTip: "'I'll take them!' = me los llevo! 💳. The most classic phrase when buying."
        },
        {
          type: "mc",
          q: "At a café: 'What would you like?' 'I'll ___ a latte, please.'",
          options: ["grab", "go", "do"],
          a: "grab",
          tip: "'Grab a coffee/latte' = very casual way to order at a café.",
          successTip: "'Grab a latte' = pedir café casualmente ☕. Super casual and friendly. 'Grab' implies speed and informality. 'I'll grab a latte' works at cafés and informal settings. In a formal restaurant, use 'I'll have' instead. Context matters!"
        },
        {
          type: "match",
          q: "Match restaurant and shopping phrases:",
          pairs: [
            { left: "Ordering food", right: "I'll have the fish" },
            { left: "Buying clothes", right: "I'll take it" },
            { left: "Declining", right: "I'll pass" }
          ],
          a: "completed",
          successTip: "Three essential consumer phrases! 🍽️ 'I'll have' = ordering food. 🛍️ 'I'll take it' = buying something. 🚫 'I'll pass' = politely declining. All three use WILL for instant decisions. Master these and you'll handle any restaurant or shop in an English-speaking country!"
        },
        {
          type: "order",
          q: "Traduce: ¿Puedo probármelo? Sí, me lo llevaré.",
          words: ["Can", "I", "try", "it", "on?", "Yes,", "I'll", "take", "it."],
          a: "Can I try it on? Yes, I'll take it.",
          extra: ["going", "buy", "to"],
          successTip: "Shopping sequence: try on → decide → take it 👍. 'Can I try it on?' (question to staff) → 'Yes, I'll take it' (instant decision after trying). This is the most natural shopping conversation in English. Two sentences — perfectly connected!"
        }
      ]
    },

    // ===================================================================
    // SET 8: SOCIAL INVITATIONS & PLANS
    // ===================================================================
    {
      id: "m6-u3-s8",
      title: "Set 8: Social Invitations",
      context: `*Toronto — Week 9, Friday*

Sofía's social circle is growing. Dan is throwing a birthday party.
"I'll be there!" Sofía replies instantly when she gets the invitation.
Her classmate Amara can't make it. "I'm sorry, I'll have to take a rain check," she says.
Sofía now understands both: accepting enthusiastically and declining politely.

*Toronto — Semana 9, viernes*

El círculo social de Sofía está creciendo. Dan organiza una fiesta de cumpleaños.
"¡Ahí estaré!" responde Sofía al instante cuando recibe la invitación.
Su compañera Amara no puede ir. "Lo siento, tendrá que ser para otra vez," dice.
Sofía ahora entiende ambas situaciones: aceptar con entusiasmo y declinar con educación.`,

      questions: [
        {
          type: "mc",
          q: "'Want to come to my party?' '___'",
          options: ["Sure! I'll be there!", "Sure! I'm going to be there!", "Sure! I am there!"],
          a: "Sure! I'll be there!",
          tip: "Instant acceptance of invitation = WILL. You decided the moment they asked.",
          successTip: "'I'll be there!' = estaré ahí 🎉. Enthusiastic instant commitment. 'I'm going to be there' sounds like you had already planned to attend before they even asked — which is odd! WILL shows you decided right now. Natural, warm, and very common!"
        },
        {
          type: "order",
          q: "Traduce: Claro, cuenten conmigo.",
          words: ["Sure,", "count", "me", "in!"],
          a: "Sure, count me in!",
          extra: ["I'll", "be", "there"],
          tip: "'Count me in' = idiomatic way to enthusiastically accept an invitation.",
          successTip: "'Count me in!' = ¡Apúntame! / ¡Cuéntenme! ✅. Very enthusiastic acceptance. Opposite: 'Count me out' = no cuenten conmigo."
        },
        {
          type: "mc",
          q: "'Wanna hang out this weekend?' '___'",
          options: ["I'm in!", "I'll in!", "I'm going!"],
          a: "I'm in!",
          tip: "'I'm in' = super casual and enthusiastic acceptance. Very informal.",
          successTip: "'I'm in!' = ¡Me apunto! / ¡Cuéntenme! 👍. Very casual acceptance. Opposite: 'I'm out' = no voy. 'I'm in' doesn't use WILL because it's a state ('I am in'), not a decision verb. But it expresses the same spontaneous acceptance. Very common among friends!"
        },
        {
          type: "input",
          q: "'Are you coming to the concert?' 'Yeah, I'll ___ you there!'",
          a: "see",
          alts: ["See", "meet", "Meet", "catch", "Catch"],
          tip: "'See you there' / 'meet you there' / 'catch you there' — all natural confirmations.",
          successTip: "'I'll see you there!' = ¡Nos vemos ahí! 👋. Classic confirmation. 'See', 'meet', and 'catch' all work here. 'Catch you there' is the most casual. 'See you there' is the most common. All use WILL — instant decision to meet at the venue. Very friendly!"
        },
        {
          type: "mc",
          q: "'Let's grab lunch tomorrow.' '___'",
          options: ["Sounds good! I'm game!", "Sounds good! I'll game!", "Sounds good! I game!"],
          a: "Sounds good! I'm game!",
          tip: "'I'm game' = I'm up for it. Informal enthusiastic agreement.",
          successTip: "'I'm game!' = ¡Me apunto! / ¡Estoy listo! 🎯. Very informal and enthusiastic. Similar to 'I'm in!' but slightly more energetic. 'Are you game?' = ¿Te animas? Very common in casual English!"
        },
        {
          type: "order",
          q: "Traduce: Lo siento, no puedo. Tendré que cancelar.",
          words: ["Sorry,", "I", "can't.", "I'll", "have", "to", "cancel."],
          a: "Sorry, I can't. I'll have to cancel.",
          extra: ["going", "must", "to"],
          tip: "'I'll have to cancel' = instant decision to cancel. Use WILL.",
        },
        {
          type: "mc",
          q: "'Can you make it to the party?' 'Sorry, I think I'll have to ___.'",
          options: ["take a rain check", "cancel them", "say that no"],
          a: "take a rain check",
          tip: "'Take a rain check' = reschedule politely. Shows you WANT to go but can't.",
          successTip: "'I'll take a rain check' = posponer educadamente 🎟️. More polite than just cancelling — it implies you want to reschedule. Shows respect for the invitation. Very common and appreciated in North American culture. Always sounds more considerate than just 'no'!"
        },
        {
          type: "input",
          q: "'Movie tonight?' 'Can't tonight, but I'll ___ up with you this weekend!'",
          a: "catch",
          alts: ["Catch", "meet", "Meet", "link", "Link"],
          tip: "'Catch up' = phrasal verb meaning to meet and spend time together.",
          successTip: "'Catch up' = ponerse al día / verse 🤝. 'I'll catch up with you' = nos veremos/pondremos al día."
        },
        {
          type: "match",
          q: "Match social responses:",
          pairs: [
            { left: "Accept invitation", right: "Count me in!" },
            { left: "Enthusiastic yes", right: "I'm game!" },
            { left: "Polite decline", right: "I'll take a rain check" }
          ],
          a: "completed",
          successTip: "Social vocabulary is crucial for making friends! ✅ 'Count me in' = clear acceptance. 🎯 'I'm game' = enthusiastic agreement. 📅 'Rain check' = polite decline with future intention. Knowing all three shows social and linguistic intelligence — you adapt to every situation!"
        },
        {
          type: "order",
          q: "Traduce: Me encantaría ir, pero tendré que dejarlo pasar esta vez.",
          words: ["I'd", "love", "to,", "but", "I'll", "have", "to", "pass", "this", "time."],
          a: "I'd love to, but I'll have to pass this time.",
          extra: ["going", "must", "can't"],
         }
      ]
    },

    // ===================================================================
    // SET 9: DAILY LIFE DECISIONS
    // ===================================================================
    {
      id: "m6-u3-s9",
      title: "Set 9: Daily Life Decisions",
      context: `*Toronto — Week 10, a typical Tuesday*

Sofía's daily life in Toronto is full of small instant decisions.
She wakes up and checks the weather: "It's cold, so I'll wear my big coat."
At the coffee shop: "I'll grab a flat white, please."
At the library: "I think I'll stay an extra hour to study."
Small decisions — all made in the moment with WILL.

*Toronto — Semana 10, un martes típico*

La vida diaria de Sofía en Toronto está llena de pequeñas decisiones instantáneas.
Se despierta y revisa el clima: "Hace frío, así que me pondré el abrigo grande."
En la cafetería: "Tomaré un flat white, por favor."
En la biblioteca: "Creo que me quedaré una hora más estudiando."
Pequeñas decisiones — todas tomadas en el momento con WILL.`,

      questions: [
        {
          type: "mc",
          q: "Sofía wakes up and it's raining. She says: 'It's raining, so I'll ___ my umbrella.'",
          options: ["grab", "take", "bring"],
          a: "grab",
          tip: "'Grab' = take quickly and informally. Most natural for a quick morning decision.",
          successTip: "'Grab my umbrella' = agarrar el paraguas 🌂. 'Grab' implies speed — a quick morning action. 'Take' and 'bring' also work, but 'grab' sounds most natural for spontaneous daily decisions. 'Grab your keys' / 'grab a jacket' / 'grab lunch' — all very common!"
        },
        {
          type: "order",
          q: "Traduce: Hace frío. Creo que me pondré el abrigo grande.",
          words: ["It's", "cold.", "I", "think", "I'll", "put", "on", "my", "big", "coat."],
          a: "It's cold. I think I'll put on my big coat.",
          extra: ["wearing", "going", "to"],
          tip: "'Put on' = phrasal verb for wearing clothes. Triggered by noticing the cold.",
          successTip: "Weather → clothing decision = very natural pattern ❄️🧥. 'It's cold' (observation) → 'I think I'll put on my coat' (instant decision). This pattern happens every day in real life! 'I think I'll' = slightly tentative, still deciding as you speak. Very authentic!"
        },
        {
          type: "input",
          q: "The queue at the coffee shop is too long. I think I'll ___ it.",
          a: "skip",
          alts: ["Skip", "pass", "Pass", "miss", "Miss"],
          tip: "'Skip it' = not bother with it. Common for minor daily decisions.",
          successTip: "'I'll skip it' = me lo salto / paso de ello 🚫. Very casual daily decision."
        },
        {
          type: "mc",
          q: "Sofía checks her fridge. It's empty. She says: '___'",
          options: ["I'll do some grocery shopping.", "I'm going to do grocery shopping.", "I do grocery shopping."],
          a: "I'll do some grocery shopping.",
          tip: "She just noticed the empty fridge = instant decision = WILL.",
          successTip: "'Grocery shopping' = hacer las compras del supermercado 🛒. 'I'll do some grocery shopping' = instant decision on seeing the empty fridge. Notice 'some' — it softens the amount. 'Do the groceries' and 'do some grocery shopping' both natural in daily English!"
        },
        {
          type: "order",
          q: "Traduce: Olvidé mi contraseña. Llamaré al soporte técnico.",
          words: ["I", "forgot", "my", "password.", "I'll", "call", "tech", "support."],
          a: "I forgot my password. I'll call tech support.",
          extra: ["going", "to", "am"],
          tip: "Problem (forgot password) → SO implied → instant decision (I'll call).",
          successTip: "'Tech support' = soporte técnico 💻."
        },
        {
          type: "mc",
          q: "It's noon and Sofía is hungry. Her lunch options are: eat at the café OR make a sandwich. She decides quickly: '___'",
          options: ["I think I'll grab something from the café.", "I'm going to grab something from the café.", "I grab something from the café."],
          a: "I think I'll grab something from the café.",
          tip: "Not 100% decided yet, considering options = 'I think I'll'.",
          successTip: "'I think I'll grab something' = tentative instant decision 🥙. She's considering options — not 100% decided. 'I think I'll' = the perfect phrase for when you're leaning towards something but still open. Very natural for small daily choices like lunch!"
        },
        {
          type: "input",
          q: "Sofía feels tired after class. 'I think I'll ___ a nap when I get home.'",
          a: "take",
          alts: ["Take", "have", "Have", "get", "Get"],
          tip: "'Take a nap' or 'have a nap' = echar una siesta. Both natural.",
          successTip: "'Take a nap' = US English 😴. 'Have a nap' = UK English. Both are correct and natural!"
        },
        {
          type: "match",
          q: "Match daily problems to instant WILL decisions:",
          pairs: [
            { left: "Phone battery dying", right: "I'll charge it now" },
            { left: "Running late", right: "I'll take a shortcut" },
            { left: "Empty fridge", right: "I'll order in" }
          ],
          a: "completed",
          successTip: "Three modern daily phrases! 🔌 'Charge it' = cargar el teléfono. 🏃 'Take a shortcut' = tomar un atajo. 🍕 'Order in' = pedir comida a domicilio (opposite of 'eat out'). All instant decisions triggered by everyday problems. Perfect WILL + daily life vocabulary!"
        },
        {
          type: "mc",
          q: "Sofía's friend asks her to bring snacks to the party. She instantly says: '___'",
          options: ["Sure, I'll bring some chips!", "Sure, I'm going to bring some chips!", "Sure, I bring some chips!"],
          a: "Sure, I'll bring some chips!",
          tip: "Instant commitment in response to a request = WILL.",
         },
        {
          type: "order",
          q: "Traduce: No hay nada en Netflix. Creo que saldré a dar un paseo.",
          words: ["There's", "nothing", "on", "Netflix.", "I", "think", "I'll", "go", "for", "a", "walk."],
          a: "There's nothing on Netflix. I think I'll go for a walk.",
          extra: ["so", "going", "to"],
          successTip: "'Go for a walk' = salir a caminar 🚶. Very natural phrase for a casual outdoor decision. Compare: 'go for a walk' / 'go for a run' / 'go for a swim' / 'go for a drive' — all use 'go for + activity'. Master this pattern! Very common in daily English conversation!"
        }
      ]
    },
  ],

  // ===================================================================
  // READING SET
  // ===================================================================
  readingSet: {
    id: "m6-u3-reading",
    title: "Reading: Sofía's Day of Instant Decisions",
    context: `*Toronto — Week 11*

Read Sofía's diary entry about a day full of spontaneous decisions.

---

**Tuesday was a day of non-stop decisions!**

I woke up late — the alarm didn't go off. "I'll skip breakfast and grab a coffee on the way," I thought. So I did.

At university, Leo forgot his notebook. "I'll lend you mine," I said immediately. Professor Chen looked impressed.

After class, it started raining heavily. I didn't have an umbrella. Priya texted: "Where are you?" I replied: "I'm soaked! I think I'll wait here until it stops." 

Then Dan called: "Want to come to the gym?" The rain was still going. "In that case, yes! I'll come," I said. Decision made.

At the gym café, the barista asked what I'd like. "I'll have a green tea, please." Easy.

On the way home, I passed a bookshop window. There was a book called *Quick English Grammar*. "I'll get it!" I thought instantly.

That night, Priya asked: "Want to watch a movie?" I was exhausted. "I think I'll call it a night, actually," I said. 

Seven instant decisions. Seven times I used WILL. Sofía: 1 — Toronto: 0.

---

*Martes fue un día de decisiones sin parar.*

*Me desperté tarde — no sonó la alarma. "Me saltaré el desayuno y tomaré un café de camino", pensé. Y así lo hice.*

*En la universidad, Leo olvidó su cuaderno. "Te presto el mío", dije inmediatamente. La profesora Chen parecía impresionada.*

*Después de clase, comenzó a llover fuerte. No tenía paraguas. Priya me escribió: "¿Dónde estás?" Respondí: "¡Estoy empapada! Creo que esperaré aquí hasta que pare."*

*Luego llamó Dan: "¿Quieres venir al gimnasio?" La lluvia seguía. "En ese caso, ¡sí! Voy", dije. Decisión tomada.*

*En la cafetería del gimnasio, el barista preguntó qué quería. "Tomaré un té verde, por favor." Fácil.*

*De camino a casa, pasé frente a la vitrina de una librería. Había un libro llamado Quick English Grammar. "¡Me lo llevo!" pensé al instante.*

*Esa noche, Priya preguntó: "¿Quieres ver una película?" Estaba agotada. "Creo que ya me retiro, la verdad", dije.*

*Siete decisiones instantáneas. Siete veces usé WILL. Sofía: 1 — Toronto: 0.*`,

    questions: [
      {
        type: "mc",
        q: "Why did Sofía skip breakfast?",
        options: ["She wasn't hungry.", "She woke up late.", "The café was closed."],
        a: "She woke up late.",
        tip: "Read carefully — the alarm didn't go off.",
        successTip: "Waking up late = classic trigger for instant decisions ⏰! 'The alarm didn't go off' → 'I'll skip breakfast' = instant consequence. This is exactly the SO pattern (even without 'so'): cause → effect → instant WILL decision. Very real, very common!"
      },
      {
        type: "input",
        q: "Leo forgot his notebook. Sofía said: 'I'll ___ you mine.'",
        a: "lend",
        alts: ["Lend", "give", "Give", "show", "Show"],
        tip: "'Lend' = prestar. 'Borrow' = pedir prestado. They're different!",
        successTip: "'Lend' vs 'Borrow' = the most confused pair in English! 📚 'I'll lend you mine' = yo te presto el mío. 'Can I borrow yours?' = ¿Puedo pedirte prestado el tuyo? Lend = give temporarily. Borrow = receive temporarily. Master this distinction — it comes up constantly!"
      },
      {
        type: "mc",
        q: "Why did Sofía decide to go to the gym?",
        options: ["She wanted to exercise.", "The rain was a reason to say yes.", "Dan convinced her."],
        a: "The rain was a reason to say yes.",
        tip: "'In that case, yes!' — she used the rain as justification.",
        successTip: "'In that case' shows Sofía adapted to the situation 🏋️. The rain prevented other plans → going to the gym made perfect sense. 'In that case' = elegant connector for when circumstances change your decision. Very natural and sophisticated English!"
      },
      {
        type: "order",
        q: "Reconstruct what Sofía said at the gym café:",
        words: ["I'll", "have", "a", "green", "tea,", "please."],
        a: "I'll have a green tea, please.",
        extra: ["get", "want", "going"],
        tip: "Standard ordering phrase at a café or restaurant.",
        successTip: "'I'll have a green tea, please' = perfect café order 🍵. 'Please' at the end = very polite. 'I'll have...' = instant decision. This is the most useful ordering phrase in the English-speaking world. Learn it, own it, use it every time you order anything!"
      },
      {
        type: "mc",
        q: "What phrase did Sofía use to decide NOT to watch the movie?",
        options: ["I think I'll call it a night.", "I'll pass.", "I won't watch it."],
        a: "I think I'll call it a night.",
        tip: "'Call it a night' = idiomatic expression meaning to stop and go to sleep.",
        successTip: "'I think I'll call it a night' = perfect polite exit 🌙. 'I think I'll' softens the decision. 'Call it a night' is idiomatic and warm — not abrupt. Compare: 'I won't watch it' (abrupt/rude) vs 'I think I'll call it a night' (polite and friendly). Always choose the idiomatic version!"
      },
      {
        type: "match",
        q: "Match Sofía's situations to the decisions she made:",
        pairs: [
          { left: "Woke up late", right: "I'll skip breakfast" },
          { left: "Rain prevented other plans", right: "In that case, I'll go to the gym" },
          { left: "Feeling exhausted at night", right: "I think I'll call it a night" }
        ],
        a: "completed",
        successTip: "Three different WILL patterns in one diary entry! 💡 'I'll skip breakfast' = instant reaction to problem. 🎩 'In that case' = elegant adapter. 🌙 'I think I'll call it a night' = tentative evening decision. Real English uses all three patterns naturally in one day!"
      }
    ]
  },

    // ===================================================================
  // READING SET
  // ===================================================================
  readingSet: {
    id: "m6-u3-reading",
    title: "Reading: Sofía's Day of Instant Decisions",
    category: "reading",
    questions: [

      // ---------------------------------------------------------------
      // PASSAGE 1: Morning decisions
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Tuesday was a day of non-stop decisions!**

I woke up late — the alarm didn't go off. "I'll skip breakfast and grab a coffee on the way," I thought. So I did.

At university, Leo forgot his notebook. "I'll lend you mine," I said immediately. Professor Chen looked impressed.

---

*Martes fue un día de decisiones sin parar.*

*Me desperté tarde — no sonó la alarma. "Me saltaré el desayuno y tomaré un café de camino", pensé. Y así lo hice.*

*En la universidad, Leo olvidó su cuaderno. "Te presto el mío", dije inmediatamente. La profesora Chen parecía impresionada.*`
      },
      {
        type: "mc",
        q: "Why did Sofía skip breakfast?",
        options: ["She wasn't hungry.", "She woke up late.", "The café was closed."],
        a: "She woke up late.",
        tip: "Read carefully — the alarm didn't go off.",
        successTip: "Waking up late = classic trigger for an instant decision ⏰! 'The alarm didn't go off' → 'I'll skip breakfast' = instant consequence. This is the SO pattern (even without 'so'): cause → instant WILL decision. Very real and very common in daily English!"
      },
      {
        type: "input",
        q: "Leo forgot his notebook. Sofía said: 'I'll ___ you mine.'",
        a: "lend",
        alts: ["Lend", "give", "Give"],
        tip: "'Lend' = prestar. 'Borrow' = pedir prestado. They're different!",
        successTip: "'Lend' vs 'Borrow' = the most confused pair in English! 📚 'I'll lend you mine' = yo te presto el mío. 'Can I borrow yours?' = ¿Puedo pedirte prestado el tuyo? Lend = give temporarily. Borrow = receive temporarily. Master this distinction — it comes up constantly!"
      },
      {
        type: "mc",
        q: "Sofía says 'I'll lend you mine' rather than 'I'm going to lend you mine' because:",
        options: [
          "She decided the moment she heard Leo had no notebook.",
          "She had planned to lend her notebook before arriving at university.",
          "The professor told her to share."
        ],
        a: "She decided the moment she heard Leo had no notebook.",
        tip: "Instant decision triggered by a situation right now = WILL.",
        successTip: "WILL = decision made at the moment of speaking 💡. Sofía didn't plan this — she heard the problem and reacted instantly. That's exactly what 'I'll' is for. 'I'm going to' would suggest she had planned to lend it before even arriving — which makes no sense here!"
      },

      // ---------------------------------------------------------------
      // PASSAGE 2: Afternoon decisions
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `After class, it started raining heavily. I didn't have an umbrella. Priya texted: "Where are you?" I replied: "I'm soaked! I think I'll wait here until it stops."

Then Dan called: "Want to come to the gym?" The rain was still going. "In that case, yes! I'll come," I said. Decision made.

At the gym café, the barista asked what I'd like. "I'll have a green tea, please." Easy.

---

*Después de clase, comenzó a llover fuerte. No tenía paraguas. Priya me escribió: "¿Dónde estás?" Respondí: "¡Estoy empapada! Creo que esperaré aquí hasta que pare."*

*Luego llamó Dan: "¿Quieres venir al gimnasio?" La lluvia seguía. "En ese caso, ¡sí! Voy", dije. Decisión tomada.*

*En la cafetería del gimnasio, el barista preguntó qué quería. "Tomaré un té verde, por favor." Fácil.*`
      },
      {
        type: "mc",
        q: "Sofía says 'I think I'll wait here' instead of 'I'll wait here'. What does 'I think' add?",
        options: [
          "It makes the decision sound tentative — she's still considering.",
          "It makes the decision stronger and more certain.",
          "It means she already planned to wait before it rained."
        ],
        a: "It makes the decision sound tentative — she's still considering.",
        tip: "'I think I'll' = leaning towards a decision but still open. Softer than a firm 'I'll'.",
        successTip: "'I think I'll...' = tenative instant decision 🤔. Still WILL, but with a softer edge. Compare: 'I'll wait here' (decided!) vs 'I think I'll wait here' (leaning towards it). Both are spontaneous — 'I think' just adds a layer of uncertainty. Very natural for things like weather!"
      },
      {
        type: "mc",
        q: "Why did Sofía decide to go to the gym?",
        options: [
          "She had already planned to exercise that afternoon.",
          "The rain made going elsewhere impossible, so the gym made sense.",
          "Dan convinced her after a long conversation."
        ],
        a: "The rain made going elsewhere impossible, so the gym made sense.",
        tip: "'In that case, yes!' — Sofía adapted her decision based on the rain situation.",
        successTip: "'In that case' = Sofía adapted to circumstances 🏋️🌧️. The rain closed other options → gym became the logical choice. 'In that case, I'll come' = polished, elegant adaptation. This is what 'In that case' is for: when a new situation changes (or clarifies) your decision!"
      },
      {
        type: "order",
        q: "Reconstruct what Sofía said at the gym café:",
        words: ["I'll", "have", "a", "green", "tea,", "please."],
        a: "I'll have a green tea, please.",
        extra: ["get", "want", "going"],
        tip: "Standard ordering phrase at any café or restaurant.",
        successTip: "'I'll have a green tea, please' = perfect café order ☕. 'Please' at the end = polite. 'I'll have...' = instant decision. This is the most universal ordering phrase in the English-speaking world — restaurants, cafés, bars, airlines. Learn it once, use it everywhere!"
      },
      {
        type: "mc",
        q: "At the café, Sofía uses 'I'll have' and not 'I'm going to have' because:",
        options: [
          "She is deciding right there at the counter.",
          "She researched the menu and planned her order before arriving.",
          "Green tea is the only option available."
        ],
        a: "She is deciding right there at the counter.",
        tip: "Ordering = instant decision made at the moment of speaking = WILL.",
        successTip: "Restaurant and café orders = always WILL ☕. 'I'll have...' sounds spontaneous and natural. 'I'm going to have...' would suggest she planned her tea order before arriving — which sounds odd! Instant decisions at counters and tables = WILL, every time, in every English-speaking country."
      },

      // ---------------------------------------------------------------
      // PASSAGE 3: Evening decisions
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `On the way home, I passed a bookshop window. There was a book called *Quick English Grammar*. "I'll get it!" I thought instantly.

That night, Priya asked: "Want to watch a movie?" I was exhausted. "I think I'll call it a night, actually," I said.

Seven instant decisions. Seven times I used WILL. Sofía: 1 — Toronto: 0.

---

*De camino a casa, pasé frente a la vitrina de una librería. Había un libro llamado Quick English Grammar. "¡Me lo llevo!" pensé al instante.*

*Esa noche, Priya preguntó: "¿Quieres ver una película?" Estaba agotada. "Creo que ya me retiro, la verdad", dije.*

*Siete decisiones instantáneas. Siete veces usé WILL. Sofía: 1 — Toronto: 0.*`
      },
      {
        type: "input",
        q: "Sofía sees the book and thinks: 'I'll ___ it!' (buy it quickly and impulsively)",
        a: "get",
        alts: ["Get", "buy", "Buy", "take", "Take", "grab", "Grab"],
        tip: "'Get', 'buy', 'take', and 'grab' all work here — all natural spontaneous purchase words.",
        successTip: "Impulse buy vocabulary 🛍️: 'I'll grab it!' (most impulsive). 'I'll get it!' (casual). 'I'll take it!' (classic in-store phrase). 'I'll buy it!' (straightforward). All are correct instant decisions — WILL in action at the bookshop window!"
      },
      {
        type: "mc",
        q: "What phrase did Sofía use to politely decline watching a movie?",
        options: [
          "I think I'll call it a night.",
          "I'll pass on that.",
          "I won't watch it."
        ],
        a: "I think I'll call it a night.",
        tip: "'Call it a night' = idiomatic expression for stopping activities and going to sleep.",
        successTip: "'I think I'll call it a night' = perfect polite exit 🌙. 'I think I'll' softens it, and 'call it a night' is warm and idiomatic — not abrupt. Compare: 'I won't watch it' (sounds rude) vs 'I think I'll call it a night' (sounds friendly and considerate). Always go idiomatic!"
      },
      {
        type: "match",
        q: "Match each of Sofía's situations to the WILL pattern she used:",
        pairs: [
          { left: "Woke up late → skipped breakfast", right: "I'll skip it (instant reaction)" },
          { left: "Rain → chose gym instead", right: "In that case, I'll come (adapter)" },
          { left: "Exhausted at night → declined movie", right: "I think I'll call it a night (tentative)" }
        ],
        a: "completed",
        successTip: "Three WILL patterns, one real day 🎯! ⚡ Instant reaction: 'I'll skip breakfast.' 🎩 Elegant adapter: 'In that case, I'll come.' 🤔 Tentative decision: 'I think I'll call it a night.' Real English uses all three naturally in a single day — and now you recognise all of them!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m6-u3-review",
    title: "Review: Making Quick Decisions",
    questions: [
      {
        type: "mc",
        q: "Which sentence shows an INSTANT decision made while speaking?",
        options: ["I'll answer the phone!", "I'm going to answer the phone.", "I answered the phone."],
        a: "I'll answer the phone!",
        tip: "Instant decisions = WILL (I'll). Pre-made plans = GOING TO.",
        successTip: "WILL = instant decision made RIGHT NOW 💡. 'I'll answer it!' = you decided in this second. GOING TO = you already planned before. This is the core rule of the whole unit — and you know it!"
      },
      {
        type: "match",
        q: "Match the connector to its register:",
        pairs: [
          { left: "so", right: "Casual / informal" },
          { left: "In that case,", right: "Polite / formal" },
          { left: "I think I'll...", right: "Tentative / considering" }
        ],
        a: "completed",
        successTip: "Three connectors — three registers! 🗣️ 'So' = everyday casual speech. 'In that case' = polite, educated, formal. 'I think I'll' = you're still deciding. Choosing the right connector shows real social intelligence in English. You now know all three!"
      },
      {
        type: "order",
        q: "Traduce: No funciona el auto, así que tomaré el metro.",
        words: ["The", "car", "won't", "start,", "so", "I'll", "take", "the", "subway."],
        a: "The car won't start, so I'll take the subway.",
        extra: ["because", "but", "therefore"],
        tip: "Problem + SO + instant decision = perfect WILL structure.",
        successTip: "'The car won't start, so I'll take the subway' = perfect everyday English 🚇. Problem → SO → instant solution. 'Subway' = US English for underground train. UK English = 'the tube' or 'the underground'. In Canada (Toronto) = 'the subway'. All correct!"
      },
      {
        type: "input",
        q: "At the restaurant: 'Are you ready?' 'Yes, I'll ___ the pasta.'",
        a: "have",
        alts: ["Have", "get", "Get", "try", "Try", "go for"],
        tip: "'I'll have...' = standard ordering phrase. 'Try/get/go for' also work.",
        successTip: "Restaurant ordering = always WILL ✅. 'I'll have', 'I'll try', 'I'll get', 'I'll go for' — all correct, all instant decisions. 'I'll have' is the most universal. Remember: never 'I'm going to have' in restaurants — it sounds like you planned your meal before arriving!"
      },
      {
        type: "mc",
        q: "Which shows a PRE-MADE plan (NOT an instant decision)?",
        options: ["I'm going to visit my parents this weekend — I booked the train.", "I'll visit my parents!", "I think I'll visit my parents."],
        a: "I'm going to visit my parents this weekend — I booked the train.",
        tip: "'Booked the train' = evidence of a pre-made plan = GOING TO.",
        successTip: "Booked train = pre-made plan → GOING TO ✅. The evidence (booked train) is the key. Without evidence, use WILL for predictions. With a pre-made plan, use GOING TO. This distinction is the most important grammar rule in Units 2 and 3 of Module 6!"
      },
      {
        type: "order",
        q: "Traduce: No te preocupes. Te lo prometo, no llegaré tarde.",
        words: ["Don't", "worry.", "I", "promise", "I", "won't", "be", "late."],
        a: "Don't worry. I promise I won't be late.",
        extra: ["going", "to", "am"],
        tip: "Promises = always WILL (positive) or WON'T (negative).",
        successTip: "'I promise I won't be late' = strong negative promise 🤝. WILL/WON'T for all promises. 'Don't worry' + 'I promise' + WON'T = triple reassurance. Very natural and sincere. This is a sentence you'll use in real life — job interviews, dates, meetings, appointments!"
      },
      {
        type: "mc",
        q: "You can't attend an event. The MOST polite response is:",
        options: ["I'll take a rain check!", "I can't come.", "I won't go."],
        a: "I'll take a rain check!",
        tip: "'Rain check' = reschedule politely. Shows you WANT to go but can't right now.",
        successTip: "'I'll take a rain check' = ¡Será para otra vez! 🎟️. Most polite decline. Shows interest in rescheduling — not just rejecting. Native speakers appreciate this phrase because it's kind and socially aware. 'I won't go' sounds abrupt and unfriendly by comparison!"
      },
      {
        type: "input",
        q: "You see someone struggling with boxes. You offer: 'I'll ___ you a hand!'",
        a: "give",
        alts: ["Give"],
        tip: "'Give someone a hand' = idiomatic offer of help. Very natural and warm.",
        successTip: "'Give you a hand' = ayudarte 🤚. Idiomatic and native-sounding. 'I'll give you a hand' = slightly warmer than 'I'll help you'. Idioms make your English memorable and natural. Master this one — you'll use it constantly when offering help to anyone!"
      },
      {
        type: "match",
        q: "Match the situations to the correct WILL pattern:",
        pairs: [
          { left: "Phone rings", right: "I'll answer it! (instant)" },
          { left: "No coffee in the shop", right: "In that case, I'll have tea. (adapter)" },
          { left: "Considering options", right: "I think I'll try the pasta. (tentative)" }
        ],
        a: "completed",
        successTip: "The three WILL patterns of Unit 3! 💡 Instant: 'I'll answer it!' ⚡. Adapter: 'In that case, I'll...' 🎩. Tentative: 'I think I'll...' 🤔. All use WILL — but for different reasons and with different social signals. You now recognize all three. Excellent progress!"
      },
      {
        type: "order",
        q: "Traduce: Estoy agotado. Creo que ya me retiro por esta noche.",
        words: ["I'm", "exhausted.", "I", "think", "I'll", "call", "it", "a", "night."],
        a: "I'm exhausted. I think I'll call it a night.",
        extra: ["going", "to", "sleep"],
        successTip: "'I think I'll call it a night' = perfect evening exit line 🌙. Two key elements: 'I think I'll' (tentative, polite) + 'call it a night' (idiom for ending the evening). Together they create a warm, natural, socially smooth goodbye. This is Unit 3 in one beautiful sentence — you nailed it!"
      }
    ]
  }
};
